import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthProvider';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, KeyRound, UserCog, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { formatApiErrorDetail } from '../lib/apiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Flash = ({ flash }) => {
  if (!flash) return null;
  const Icon = flash.type === 'error' ? AlertTriangle : CheckCircle2;
  return (
    <div className={`mb-4 p-3 rounded-lg border text-sm flex items-start gap-2 ${flash.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>{flash.msg}</span>
    </div>
  );
};

export const UpdateProfileForm = () => {
  const { user, authHeader, refresh } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setEmail(user.email || '');
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setFlash(null);
    try {
      const payload = {
        first_name: firstName,
        last_name: lastName,
        email: email.toLowerCase().trim(),
      };
      const { data } = await axios.put(`${API}/auth/me`, payload, { headers: authHeader() });
      // If email changed, server returns a new token bound to the new email
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
      }
      localStorage.setItem('user', JSON.stringify(data.user));
      await refresh();
      setFlash({ type: 'success', msg: 'Profile updated.' });
    } catch (err) {
      setFlash({ type: 'error', msg: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200" data-testid="update-profile-card">
      <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><UserCog className="w-5 h-5 text-blue-600" />Profile</h2>
      <p className="text-sm text-gray-500 mt-1 mb-4">Update your name or email. Changing your email signs you in again under the new address.</p>
      <Flash flash={flash} />
      <form onSubmit={handleSubmit} className="space-y-3" data-testid="update-profile-form">
        <div className="grid grid-cols-2 gap-3">
          <div><Label htmlFor="prof-first">First name</Label><Input id="prof-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} required data-testid="profile-first-name" /></div>
          <div><Label htmlFor="prof-last">Last name</Label><Input id="prof-last" value={lastName} onChange={(e) => setLastName(e.target.value)} required data-testid="profile-last-name" /></div>
        </div>
        <div><Label htmlFor="prof-email">Email</Label><Input id="prof-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="profile-email" /></div>
        <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="profile-save-btn">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Save profile'}
        </Button>
      </form>
    </section>
  );
};

export const ChangePasswordForm = () => {
  const { authHeader, updateToken } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFlash(null);
    if (newPassword.length < 8) {
      setFlash({ type: 'error', msg: 'New password must be at least 8 characters.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setFlash({ type: 'error', msg: 'New password and confirmation do not match.' });
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      }, { headers: authHeader() });
      // Persist the fresh token (bound to the new token_version) so this tab stays signed in.
      if (data.access_token) updateToken(data.access_token);
      setFlash({ type: 'success', msg: data.message || 'Password updated.' });
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      setFlash({ type: 'error', msg: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200" data-testid="change-password-card">
      <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><KeyRound className="w-5 h-5 text-blue-600" />Change password</h2>
      <p className="text-sm text-gray-500 mt-1 mb-4">Use a strong password with at least 8 characters. <span className="text-gray-600">Changing your password will sign out any other devices or browsers you&apos;re signed in on.</span></p>
      <Flash flash={flash} />
      <form onSubmit={handleSubmit} className="space-y-3 max-w-md" data-testid="change-password-form">
        <div><Label htmlFor="cp-cur">Current password</Label><Input id="cp-cur" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required data-testid="cp-current" /></div>
        <div><Label htmlFor="cp-new">New password</Label><Input id="cp-new" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8} data-testid="cp-new" /></div>
        <div><Label htmlFor="cp-conf">Confirm new password</Label><Input id="cp-conf" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8} data-testid="cp-confirm" /></div>
        <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="cp-submit">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Updating...</> : 'Update password'}
        </Button>
      </form>
    </section>
  );
};
